import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useAppSelector, useAppDispatch } from "hooks/useStore";
import { selectSources, addSource } from "store/slices/sources";
import { useResearchPaperUpload } from "features/ResearchHub/hooks/useResearchPaperUpload";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import useDataSources from "./useDataSources";

interface UseDataSourceUploadReturn {
  triggerFileSelection: () => void;
  uploadFile: (file: File) => void;
  isUploading: boolean;
}

/**
 * Hook that handles paper upload and auto-selection for the DataSourceSelector.
 * Encapsulates all upload logic including file selection, upload process,
 * and automatic addition of uploaded papers to selected sources.
 */
export const useDataSourceUpload = (): UseDataSourceUploadReturn => {
  const [recentlyUploadedPaperId, setRecentlyUploadedPaperId] = useState<
    string | null
  >(null);
  const dispatch = useAppDispatch();
  const selectedSources = useAppSelector(selectSources);
  const { dataSources: allDataSources } = useDataSources();
  const [labels] = useLabels("features.threads.data-source-selector");

  const { triggerFileSelection, isUploading, uploadFile } =
    useResearchPaperUpload({
      onUploadTriggered: () => {
        setRecentlyUploadedPaperId(null);
      },
      onSuccess: (url) => {
        toast.success(labels["upload-success"]);

        // Extract paper ID from URL like "/papers/uploaded/u:9eb06420-c05f-4891-b6ff-fa0589dff6db"
        if (url) {
          const match = url.match(/u:([a-f0-9-]+)/);
          if (match) {
            const paperId = `u:${match[1]}`;
            setRecentlyUploadedPaperId(paperId);
          }
        }
      },
      onError: (errorMessage) => {
        toast.error(labels["upload-error"]);
        console.error("Failed to upload paper:", errorMessage);
        setRecentlyUploadedPaperId(null);
      },
      actionSource: MixpanelActionSource.DataSourceSelector,
    });

  // Auto-select recently uploaded paper when it appears in data sources
  useEffect(() => {
    if (!recentlyUploadedPaperId || !allDataSources) {
      return;
    }

    // Find the uploaded paper in the data sources
    const uploadedSource = allDataSources.find(
      (ds) =>
        ds.type === "paper" && ds.paper?.paper_id === recentlyUploadedPaperId
    );

    if (uploadedSource?.paper) {
      // Check if not already selected
      const isAlreadySelected = selectedSources.some(
        (s) => s.paper_id === uploadedSource.paper?.paper_id
      );

      if (!isAlreadySelected) {
        dispatch(addSource(uploadedSource.paper));
      }

      // Clear the recently uploaded paper ID
      setRecentlyUploadedPaperId(null);
    }
  }, [recentlyUploadedPaperId, allDataSources, selectedSources, dispatch]);

  return {
    triggerFileSelection,
    uploadFile,
    isUploading,
  };
};
